import type { BasicField, AdvancedField, FieldGroup } from '../components/hierarchy/HierarchyCreationForm'
import {
  CONTEXT_OPTIONS, DEADLINE_TYPE_OPTIONS, DURATION_OPTIONS, ENERGY_OPTIONS, LIFE_AREA_OPTIONS,
  MATTERS_OPTIONS, PRIORITY_OPTIONS, STATUS_OPTIONS, WHEN_OPTIONS, lifeAreaLabel, lifeAreaValue, titleHint, type TaskFormData,
} from '../utils/taskForm'

/**
 * Decision Signal Capture: manual "Decision details" fields. Editing any of
 * these writes the value through the normal create/update path, which stamps
 * provenance source=user (trust 1.0) so a user correction always wins over an
 * LLM inference. Mirrors the catalog tiers in
 * `src/domains/shared_utilities/services/decision_fields.py`.
 */
const _level = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
]
const _yesNo = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]

const energyField: AdvancedField = {
  name: 'energy_level_required', label: 'Energy needed', type: 'select',
  options: _level, section: 'advanced',
}
const emotionalLoadField: AdvancedField = {
  name: 'emotional_load', label: 'Emotional load (0 light - 10 heavy)',
  type: 'number', min: 0, max: 10, section: 'advanced',
}
const difficultyField: AdvancedField = {
  name: 'difficulty', label: 'Difficulty', type: 'select',
  options: [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' },
  ],
  section: 'advanced',
}
const deadlineTypeField: AdvancedField = {
  name: 'deadline_type', label: 'Deadline type', type: 'select',
  options: [
    { value: 'soft', label: 'Soft / flexible' },
    { value: 'hard', label: 'Hard / fixed' },
  ],
  section: 'advanced',
}
const deadlineConsequenceField: AdvancedField = {
  name: 'deadline_consequence', label: 'What happens if this is delayed?',
  type: 'text', placeholder: 'e.g., I miss the interview window', section: 'advanced',
}
const deadlineRiskField: AdvancedField = {
  name: 'deadline_risk_level', label: 'Deadline risk', type: 'select',
  options: _level, section: 'advanced',
}
const starterStepField: AdvancedField = {
  name: 'minimum_starter_step', label: 'Smallest version that still counts',
  type: 'text', placeholder: 'e.g., open the doc and write one line', section: 'advanced',
}
const isQuickWinField: AdvancedField = {
  name: 'is_quick_win', label: 'Quick win?', type: 'select',
  options: _yesNo, section: 'advanced',
}
const motivationField: AdvancedField = {
  name: 'motivation', label: 'Why it matters', type: 'text',
  placeholder: 'why this matters to you', section: 'advanced',
}
const motivationLevelField: AdvancedField = {
  name: 'motivation_level', label: 'How motivating is this?', type: 'select',
  options: _level, section: 'advanced',
}
const successCriteriaField: AdvancedField = {
  name: 'success_criteria', label: 'What will be true when this is done?', type: 'text',
  section: 'advanced',
}

/** Tier-1/2 decision fields for executable items (tasks, action items). */
const execDecisionFields: AdvancedField[] = [
  energyField, emotionalLoadField, difficultyField, deadlineTypeField,
  deadlineConsequenceField, starterStepField, isQuickWinField, motivationField,
]
/** Decision fields for long-horizon items (goals, dreams). */
const outcomeDecisionFields: AdvancedField[] = [
  deadlineRiskField, motivationField, motivationLevelField, successCriteriaField,
]
const habitDecisionFields: AdvancedField[] = [
  energyField, emotionalLoadField, difficultyField,
]
const skillDecisionFields: AdvancedField[] = [energyField, difficultyField]

export const dreamFormFields: { basicFields: BasicField[]; advancedFields: AdvancedField[] } = {
  basicFields: [
    {
      name: 'title',
      label: 'Title',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., Become a Principal Engineer',
      section: 'basic' as const
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea' as const,
      placeholder: 'Describe your dream...',
      rows: 3,
      section: 'basic' as const
    }
  ],
  advancedFields: [
    {
      name: 'priority',
      label: 'Priority',
      type: 'select' as const,
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'critical', label: 'Critical' }
      ],
      section: 'advanced' as const
    },
    {
      name: 'category',
      label: 'Category',
      type: 'text' as const,
      placeholder: 'e.g., Career',
      section: 'advanced' as const
    },
    {
      name: 'target_date',
      label: 'Target Date',
      type: 'date' as const,
      section: 'advanced' as const
    },
    ...outcomeDecisionFields
  ]
}

export const aspirationFormFields: { basicFields: BasicField[]; advancedFields: AdvancedField[] } = {
  basicFields: [
    {
      name: 'title',
      label: 'Title',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., Become a Senior Engineer',
      section: 'basic' as const
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea' as const,
      placeholder: 'Describe your aspiration...',
      rows: 3,
      section: 'basic' as const
    }
  ],
  advancedFields: [
    {
      name: 'priority',
      label: 'Priority',
      type: 'select' as const,
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'critical', label: 'Critical' }
      ],
      section: 'advanced' as const
    },
    {
      name: 'category',
      label: 'Category',
      type: 'text' as const,
      placeholder: 'e.g., Career',
      section: 'advanced' as const
    },
    {
      name: 'target_date',
      label: 'Target Date',
      type: 'date' as const,
      section: 'advanced' as const
    },
    {
      // UX-7: use the shared ParentPicker like Goal/Task/ActionItem (was a plain <select>)
      name: 'parent_link',
      label: 'Parent',
      type: 'parent_picker' as const,
      allowedParentTypes: ['dream'],
      section: 'advanced' as const
    },
    ...outcomeDecisionFields
  ]
}

export const goalFormFields: { basicFields: BasicField[]; advancedFields: AdvancedField[] } = {
  basicFields: [
    {
      name: 'title',
      label: 'Title',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., Master React and TypeScript',
      section: 'basic' as const
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea' as const,
      placeholder: 'Describe your goal...',
      rows: 3,
      section: 'basic' as const
    }
  ],
  advancedFields: [
    {
      name: 'priority',
      label: 'Priority',
      type: 'select' as const,
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'critical', label: 'Critical' }
      ],
      section: 'advanced' as const
    },
    {
      name: 'category',
      label: 'Category',
      type: 'text' as const,
      placeholder: 'e.g., Technical Skills',
      section: 'advanced' as const
    },
    {
      name: 'target_date',
      label: 'Target Date',
      type: 'date' as const,
      section: 'advanced' as const
    },
    {
      name: 'parent_link',
      label: 'Parent',
      type: 'parent_picker' as const,
      allowedParentTypes: ['dream'],
      section: 'advanced' as const
    },
    ...outcomeDecisionFields
  ]
}

/**
 * Tasks — the form/field contract of 2026-09-04
 * (docs/TASK_FORM_FIELD_CONTRACT_2026-09-04.md), regrouped 2026-09-05.
 *
 * Tier A: the title, the only thing required to save. Then two groups, each
 * saying once what a per-field tag used to repeat on every row:
 *  - "Helps me plan it": how long, when (and how firm), what it belongs to or
 *    which parts of life, energy, where, priority, repeats — what Auto-Mom
 *    and the scheduler need to place it.
 *  - "Helps me motivate you when you are stuck": why it matters, the first
 *    step, what done means — the words the motivator hands back.
 * Status and progress are not asked at creation (a new task is not started,
 * at 0); reminders, notes and a saved place are added on the task itself.
 * There is no "More": the create page offers what the task page's Add row
 * offers, and nothing else.
 *
 * "When" is one chip row that writes three fields; utils/taskForm.ts holds
 * the mapping in both directions so create and the task page cannot drift.
 */
export const PLAN_GROUP: FieldGroup = {
  key: 'plan',
  title: 'Helps me plan it',
  blurb: 'What I need to find it a place in your day. Skip what you do not know yet.',
}
export const MOTIVATE_GROUP: FieldGroup = {
  key: 'motivate',
  title: 'Helps me motivate you when you are stuck',
  blurb: 'Your own words, handed back to you when it gets hard.',
}
const hasDate = (f: TaskFormData) => f.when === 'date' && Boolean(f.due_date)

export const taskFormFields: { basicFields: BasicField[]; advancedFields: AdvancedField[] } = {
  basicFields: [
    {
      name: 'title',
      label: 'What needs doing?',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., Call the dentist about the crown',
      liveHint: (v) => titleHint(String(v || '')),
      section: 'basic' as const
    },
    // ── Helps me plan it ──
    {
      name: 'estimated_duration',
      span: 'half' as const,
      label: 'How long',
      type: 'chips' as const,
      options: DURATION_OPTIONS,
      numeric: true,
      customDuration: true,
      group: PLAN_GROUP,
      section: 'basic' as const
    },
    {
      name: 'when',
      span: 'half' as const,
      label: 'When',
      type: 'chips' as const,
      options: WHEN_OPTIONS,
      group: PLAN_GROUP,
      section: 'basic' as const
    },
    {
      name: 'due_date',
      span: 'half' as const,
      label: 'Which date',
      type: 'date' as const,
      showWhen: (f) => f.when === 'date',
      group: PLAN_GROUP,
      section: 'basic' as const
    },
    {
      name: 'due_time',
      span: 'half' as const,
      label: 'Time (optional)',
      type: 'time' as const,
      helpText: 'Set a time and I will hold you to it as the deadline nears.',
      showWhen: hasDate,
      group: PLAN_GROUP,
      section: 'basic' as const
    },
    {
      name: 'deadline_type',
      span: 'half' as const,
      label: 'How firm is that date?',
      type: 'chips' as const,
      options: DEADLINE_TYPE_OPTIONS,
      showWhen: hasDate,
      group: PLAN_GROUP,
      section: 'basic' as const
    },
    {
      name: 'deadline_consequence',
      label: 'If the time passes…',
      type: 'text' as const,
      placeholder: 'e.g., I miss the enrolment window',
      showWhen: hasDate,
      group: PLAN_GROUP,
      section: 'basic' as const
    },
    {
      name: 'parent_link',
      span: 'half' as const,
      label: 'Belongs to',
      type: 'parent_picker' as const,
      allowedParentTypes: ['goal', 'skill'],
      group: PLAN_GROUP,
      section: 'basic' as const
    },
    // Owner 2026-09-05: selectable and addable — a person's own areas come back
    // as chips (CreateTask feeds the options from useLifeAreaOptions).
    {
      name: 'life_areas',
      label: 'Or, if it is just for you — which parts of life?',
      type: 'chips' as const,
      multi: true,
      options: LIFE_AREA_OPTIONS,
      allowCustom: true,
      customLabel: 'Another…',
      customPlaceholder: 'e.g. Side project',
      customValue: lifeAreaValue,
      labelOf: lifeAreaLabel,
      helpText: 'Pick one or more; the first is the main one. Name your own if none of these fits.',
      showWhen: (f) => !f.parent_id,
      group: PLAN_GROUP,
      section: 'basic' as const
    },
    {
      name: 'energy_level_required',
      span: 'half' as const,
      label: 'Energy it takes',
      type: 'chips' as const,
      options: ENERGY_OPTIONS,
      group: PLAN_GROUP,
      section: 'basic' as const
    },
    {
      name: 'context',
      span: 'half' as const,
      label: 'Where',
      type: 'chips' as const,
      options: CONTEXT_OPTIONS,
      group: PLAN_GROUP,
      section: 'basic' as const
    },
    {
      name: 'priority',
      span: 'half' as const,
      label: 'Priority',
      type: 'chips' as const,
      options: PRIORITY_OPTIONS,
      group: PLAN_GROUP,
      section: 'basic' as const
    },
    {
      name: 'is_recurring',
      span: 'half' as const,
      label: 'Repeats',
      type: 'recurrence' as const,
      group: PLAN_GROUP,
      section: 'basic' as const
    },
    // ── Helps me motivate you when you are stuck ──
    // A standalone task carries its own why; one under a goal inherits the
    // goal's, so the why only appears without a parent.
    {
      name: 'motivation',
      label: 'Why it matters',
      type: 'text' as const,
      placeholder: 'In your words, one line',
      showWhen: (f) => !f.parent_id,
      group: MOTIVATE_GROUP,
      section: 'basic' as const
    },
    {
      name: 'motivation_level',
      span: 'half' as const,
      label: 'How much, right now',
      type: 'chips' as const,
      options: MATTERS_OPTIONS,
      showWhen: (f) => !f.parent_id,
      group: MOTIVATE_GROUP,
      section: 'basic' as const
    },
    {
      name: 'minimum_starter_step',
      span: 'half' as const,
      label: 'First step',
      type: 'text' as const,
      placeholder: 'The first five minutes',
      group: MOTIVATE_GROUP,
      section: 'basic' as const
    },
    {
      name: 'acceptance_criteria',
      label: 'Done means',
      type: 'text' as const,
      placeholder: 'What will be true when this is done?',
      group: MOTIVATE_GROUP,
      section: 'basic' as const
    }
  ],
  // Not on the create page (added on the task itself); kept for any form that
  // still wants a collapsed section.
  advancedFields: [
    {
      name: 'hold_lead_minutes',
      span: 'half' as const,
      label: 'Start reminding (minutes before)',
      type: 'number' as const,
      placeholder: '55',
      helpText: 'How long before the deadline the reminders begin.',
      showWhen: (f) => hasDate(f) && Boolean(f.due_time),
      section: 'advanced' as const
    },
    {
      name: 'hold_interval_minutes',
      span: 'half' as const,
      label: 'Remind every (minutes)',
      type: 'number' as const,
      placeholder: '5',
      helpText: 'Minimum 5 minutes.',
      showWhen: (f) => hasDate(f) && Boolean(f.due_time),
      section: 'advanced' as const
    },
    {
      name: 'place_id',
      span: 'half' as const,
      label: 'A specific place',
      type: 'place_picker' as const,
      section: 'advanced' as const
    },
    {
      name: 'description',
      label: 'Notes',
      type: 'textarea' as const,
      rows: 3,
      placeholder: 'Anything else worth remembering',
      section: 'advanced' as const
    }
  ]
}

/** Edit only: a new task is not started, so creation never asks. */
export const taskEditOnlyFields: AdvancedField[] = [
  {
    name: 'status',
    label: 'Status',
    type: 'chips' as const,
    options: STATUS_OPTIONS,
    section: 'advanced' as const
  }
]

export const actionItemFormFields: { basicFields: BasicField[]; advancedFields: AdvancedField[] } = {
  basicFields: [
    {
      name: 'title',
      label: 'Title',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., Review pull request',
      section: 'basic' as const
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea' as const,
      required: true,
      placeholder: 'Describe what needs to be done...',
      rows: 3,
      section: 'basic' as const
    },
    {
      name: 'estimated_duration',
      label: 'Estimated Duration',
      type: 'duration' as const,
      section: 'basic' as const
    }
  ],
  advancedFields: [
    {
      name: 'priority',
      label: 'Priority',
      type: 'select' as const,
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' }
      ],
      section: 'advanced' as const
    },
    {
      name: 'due_date',
      label: 'Due Date',
      type: 'date' as const,
      section: 'advanced' as const
    },
    {
      name: 'parent_link',
      label: 'Parent',
      type: 'parent_picker' as const,
      allowedParentTypes: ['task', 'habit', 'skill', 'goal'],
      section: 'advanced' as const
    },
    {
      name: 'place_id',
      label: 'Remind me at',
      type: 'place_picker' as const,
      section: 'advanced' as const
    },
    ...execDecisionFields
  ]
}

export const roadblockFormFields: { basicFields: BasicField[]; advancedFields: AdvancedField[] } = {
  basicFields: [
    {
      name: 'title',
      label: 'Title',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., Lack of time for learning',
      section: 'basic' as const
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea' as const,
      required: true,
      placeholder: 'Describe the roadblock and its impact...',
      rows: 3,
      section: 'basic' as const
    }
  ],
  advancedFields: [
    {
      name: 'priority',
      label: 'Priority',
      type: 'select' as const,
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' }
      ],
      section: 'advanced' as const
    },
    {
      name: 'severity',
      label: 'Severity',
      type: 'select' as const,
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' }
      ],
      section: 'advanced' as const
    },
    {
      name: 'parent_link',
      label: 'Parent',
      type: 'parent_picker' as const,
      allowedParentTypes: ['goal'],
      section: 'advanced' as const
    }
  ]
}

export const skillFormFields: { basicFields: BasicField[]; advancedFields: AdvancedField[] } = {
  basicFields: [
    {
      name: 'title',
      label: 'Title',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., Public speaking',
      section: 'basic' as const
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea' as const,
      placeholder: 'What this skill means to you...',
      rows: 3,
      section: 'basic' as const
    }
  ],
  advancedFields: [
    {
      name: 'priority',
      label: 'Priority',
      type: 'select' as const,
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' }
      ],
      section: 'advanced' as const
    },
    {
      name: 'parent_link',
      label: 'Parent',
      type: 'parent_picker' as const,
      allowedParentTypes: ['goal'],
      section: 'advanced' as const
    },
    ...skillDecisionFields
  ]
}

export const habitFormFields: { basicFields: BasicField[]; advancedFields: AdvancedField[] } = {
  basicFields: [
    {
      name: 'title',
      label: 'Title',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., Morning meditation',
      section: 'basic' as const
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea' as const,
      placeholder: 'What this habit means to you...',
      rows: 3,
      section: 'basic' as const
    }
  ],
  advancedFields: [
    {
      name: 'priority',
      label: 'Priority',
      type: 'select' as const,
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' }
      ],
      section: 'advanced' as const
    },
    {
      name: 'parent_link',
      label: 'Parent',
      type: 'parent_picker' as const,
      allowedParentTypes: ['goal'],
      section: 'advanced' as const
    },
    ...habitDecisionFields
  ]
}

